import { useEffect, useState } from 'react';
import type { Audience, AddMembersReport } from '@epasu/core';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';

/**
 * Eine Auswahl von Leads als neue Lead-Liste sichern.
 *
 * Legt die Liste an und nimmt die Auswahl in einem Rutsch auf. Was dabei
 * abgewiesen wird — falsche Zielgruppe, kein Lead mehr — bekommt der
 * Bericht von `leadLists.addMembers` mit einem Grund, keinen Fehler:
 * die Liste selbst entsteht in jedem Fall.
 */
type Grund = AddMembersReport['rejected'][number]['reason'];

const GRUND_TEXT: Record<Grund, string> = {
  kein_lead: 'ist inzwischen kein Lead mehr',
  fremde_zielgruppe: 'gehört zu einer anderen Zielgruppe',
  schon_drin: 'war schon in der Liste',
  nicht_gefunden: 'wurde nicht mehr gefunden',
};

interface Props {
  contactIds: string[];
  audiences: Audience[];
  /** Vorbelegung, wenn die Leads-Seite gerade nach einer Zielgruppe filtert. */
  suggestedAudienceId?: string;
  onClose: () => void;
  onDone: () => void;
}

export function SaveAsListDialog({
  contactIds,
  audiences,
  suggestedAudienceId,
  onClose,
  onDone,
}: Props) {
  const [name, setName] = useState('');
  const [audienceId, setAudienceId] = useState(suggestedAudienceId ?? '');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bericht, setBericht] = useState<AddMembersReport | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const speichern = async () => {
    if (!name.trim() || !audienceId) return;
    setBusy(true);
    setFehler(null);
    try {
      const liste = await crm.leadLists.create({ name: name.trim(), audienceId });
      const ergebnis = await crm.leadLists.addMembers(liste.id, contactIds);
      setBericht(ergebnis);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Anlegen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const rejectedByReason = new Map<Grund, number>();
  for (const r of bericht?.rejected ?? []) {
    rejectedByReason.set(r.reason, (rejectedByReason.get(r.reason) ?? 0) + 1);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6">
      <div className="w-full max-w-md space-y-4 rounded-lg bg-card p-6 shadow-xl">
        <h2 className="text-lg font-semibold">Auswahl als Liste speichern</h2>

        {bericht ? (
          <div className="space-y-3">
            <p className="text-sm">
              <strong className="text-foreground">{bericht.added}</strong> von{' '}
              {contactIds.length} {contactIds.length === 1 ? 'Lead' : 'Leads'} aufgenommen.
            </p>
            {rejectedByReason.size > 0 && (
              <ul className="space-y-1 text-sm text-muted-foreground">
                {[...rejectedByReason].map(([grund, anzahl]) => (
                  <li key={grund}>
                    {anzahl} {anzahl === 1 ? 'Lead' : 'Leads'} {GRUND_TEXT[grund]}
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end">
              <Button onClick={onDone}>Fertig</Button>
            </div>
          </div>
        ) : (
          <>
            <label className="block space-y-1 text-sm">
              <span className="font-medium">Name</span>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="z. B. Grundschulen Nord, September"
                autoFocus
              />
            </label>

            <label className="block space-y-1 text-sm">
              <span className="font-medium">Zielgruppe</span>
              <Select value={audienceId} onChange={(e) => setAudienceId(e.target.value)}>
                <option value="">— wählen —</option>
                {audiences.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </label>

            <p className="text-xs text-muted-foreground">
              Nur Leads aus dieser Zielgruppe landen in der Liste — die übrigen der
              Auswahl bleiben draußen, mit Grund im Bericht danach.
            </p>

            {fehler && <p className="text-sm text-danger">{fehler}</p>}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={busy}>
                Abbrechen
              </Button>
              <Button
                onClick={() => void speichern()}
                disabled={busy || !name.trim() || !audienceId}
              >
                {busy ? 'Speichere …' : 'Liste anlegen'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
